import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { clinicInfo } from '@/mocks/homeData';

const missionValues = [
  {
    id: 'missao',
    title: 'Missão',
    description: 'Proporcionar cuidado integral à saúde da mulher, unindo excelência médica, tecnologia de ponta e acolhimento genuíno, em um ambiente que celebra a feminilidade em todas as suas fases.',
    icon: 'ri-focus-3-line',
  },
  {
    id: 'visao',
    title: 'Visão',
    description: 'Ser referência nacional em saúde feminina de alto padrão, transformando a experiência ginecológica em um momento de autocuidado, confiança e bem-estar.',
    icon: 'ri-eye-line',
  },
  {
    id: 'valores',
    title: 'Valores',
    description: 'Humanização, ética, inovação, respeito à individualidade, discrição e compromisso com a saúde integral da mulher em cada atendimento.',
    icon: 'ri-heart-3-line',
  },
];

const timelineItems = [
  {
    year: '2019',
    title: 'Fundação',
    description: 'Dra. Nathália Posso funda a Femme Vie com a visão de criar um espaço onde a saúde da mulher é tratada com excelência e acolhimento diferenciado.',
  },
  {
    year: '2021',
    title: 'Expansão',
    description: 'Mudança para o Duets Office Tower, no Cocó, com estrutura ampliada para oferecer exames de imagem no próprio consultório.',
  },
  {
    year: '2023',
    title: 'Nova Sócia',
    description: 'Dra. Mayna Moura integra a equipe, trazendo sua expertise em Ginecologia Regenerativa e Estética Íntima, ampliando o portfólio de serviços.',
  },
  {
    year: '2025',
    title: 'Consolidação',
    description: 'Mais de 4.200 avaliações no Google, consolidação como referência em ginecologia de alto padrão em Fortaleza.',
  },
];

export default function Sobre() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* Page Header */}
        <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-background-100 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary-400 via-transparent to-transparent" />
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 text-center">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-xs tracking-[0.2em] uppercase text-primary-400 font-label"
            >
              Nossa História
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-heading text-4xl md:text-6xl lg:text-7xl text-foreground-950 mt-4 leading-[1.05]"
            >
              Sobre a<br />
              <span className="italic font-light">Femme Vie.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="max-w-[680px] mx-auto mt-6 text-foreground-600 text-base leading-relaxed"
            >
              Nascemos do desejo de oferecer um cuidado diferente. Um espaço pensado para a mulher moderna, que busca saúde com qualidade, tecnologia e, acima de tudo, respeito à sua individualidade.
            </motion.p>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="relative bg-background-50 py-24 md:py-32 lg:py-40">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
            <div className="max-w-[800px] mx-auto">
              {timelineItems.map((item, index) => (
                <motion.div
                  key={item.year}
                  ref={index === 0 ? ref : undefined}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.15 * index }}
                  className="relative flex gap-8 pb-16 last:pb-0"
                >
                  {/* Line */}
                  {index < timelineItems.length - 1 && (
                    <div className="absolute left-[23px] top-12 bottom-0 w-[1px] bg-background-300/60" />
                  )}
                  {/* Year */}
                  <div className="relative z-10 flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-full bg-primary-50 border border-primary-200">
                    <span className="font-label text-xs font-semibold text-primary-500">{item.year.slice(2)}</span>
                  </div>
                  {/* Content */}
                  <div className="pt-1">
                    <span className="text-xs tracking-[0.12em] uppercase text-primary-400 font-label">{item.year}</span>
                    <h3 className="font-heading text-2xl text-foreground-950 mt-1 mb-2 leading-tight">{item.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission / Vision / Values */}
        <section className="relative bg-background-100 py-24 md:py-32 lg:py-40">
          <div className="absolute top-0 left-0 right-0 h-20 bg-background-50 organic-divider" />
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
              {missionValues.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.6, delay: 0.12 * index }}
                  whileHover={{ y: -6 }}
                  className="text-center group transition-all duration-500"
                >
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-50 border border-background-200/70 text-primary-500 transition-all duration-500 group-hover:bg-primary-50 group-hover:border-primary-200">
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-heading text-2xl text-foreground-950 mt-6 mb-3">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Clinic Photo */}
        <section className="relative bg-background-50 py-24 md:py-32 lg:py-40">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
              >
                <span className="text-xs tracking-[0.2em] uppercase text-primary-400 font-label">
                  Nossa Estrutura
                </span>
                <h2 className="font-heading text-4xl md:text-5xl text-foreground-950 mt-3 leading-[1.1]">
                  Um espaço pensado<br />
                  <span className="italic font-light">para você.</span>
                </h2>
                <p className="mt-6 text-foreground-600 text-sm leading-relaxed max-w-lg">
                  Localizada no coração do Cocó, na elegante Duets Office Tower, nossa clínica foi projetada para oferecer conforto e privacidade. Cada detalhe — da iluminação à escolha dos materiais — foi pensado para criar uma atmosfera de tranquilidade e bem-estar.
                </p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <div className="flex items-center gap-3 text-sm text-foreground-600">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <p className="font-label text-xs text-foreground-400">Endereço</p>
                      <p className="text-sm">{clinicInfo.address.street}</p>
                      <p className="text-xs text-foreground-500">{clinicInfo.address.complement}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
                className="relative"
              >
                <div className="relative rounded-lg overflow-hidden aspect-[4/5]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Elegant%20luxury%20medical%20clinic%20reception%20area%20with%20soft%20warm%20lighting%2C%20cream%20and%20beige%20tones%2C%20minimal%20modern%20design%2C%20fresh%20white%20orchids%2C%20marble%20accents%2C%20comfortable%20designer%20seating%2C%20premium%20boutique%20healthcare%20interior%2C%20sophisticated%20feminine%20atmosphere&width=1200&height=1500&seq=femme-sobre-clinic&orientation=portrait"
                    alt="Interior da Clínica Femme Vie"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Decorative element */}
                <div className="absolute -bottom-6 -left-6 w-32 h-32 rounded-full bg-primary-50/50 hidden lg:block" />
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative bg-background-100 py-20 md:py-28">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6 }}
              className="font-heading text-3xl md:text-5xl text-foreground-950 leading-[1.15]"
            >
              Venha nos<br />
              <span className="italic font-light">conhecer.</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-4 text-foreground-600 text-sm max-w-md mx-auto"
            >
              Agende sua consulta e descubra uma nova forma de cuidar da sua saúde.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <a
                href={`https://wa.me/${clinicInfo.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm tracking-[0.06em] uppercase bg-primary-500 text-white hover:bg-primary-600 transition-colors duration-300 whitespace-nowrap"
              >
                <i className="ri-whatsapp-line"></i>
                Agendar Consulta
              </a>
              <Link
                to="/estrutura"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm tracking-[0.06em] uppercase border border-background-300/60 text-foreground-700 hover:border-primary-300 hover:text-primary-500 transition-colors duration-300 whitespace-nowrap"
              >
                Conhecer Estrutura
                <i className="ri-arrow-right-line"></i>
              </Link>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}